import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Flame } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StreakMilestonePayload } from "@/lib/streakMilestones";

interface StreakMilestoneCelebrationProps {
  payload: StreakMilestonePayload;
  onComplete?: () => void;
}

export function StreakMilestoneCelebration({ payload, onComplete }: StreakMilestoneCelebrationProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
    const colors = ["#D97757", "#E08868", "#F5B14C"];

    confetti({ ...defaults, particleCount: 120, origin: { x: 0.5, y: 0.6 }, colors });

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount: Math.floor(particleCount),
        origin: { x: Math.random() * 0.4 + 0.1, y: Math.random() * 0.3 + 0.2 },
        colors,
      });
      confetti({
        ...defaults,
        particleCount: Math.floor(particleCount),
        origin: { x: Math.random() * 0.4 + 0.5, y: Math.random() * 0.3 + 0.2 },
        colors,
      });
    }, 250);

    const timeout = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onComplete?.(), 300);
    }, 4000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [onComplete]);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsVisible(false);
    onComplete?.();
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm",
        "animate-fade-in",
      )}
      onClick={handleDismiss}
    >
      <div
        className={cn(
          "relative bg-card border border-border rounded-2xl p-8 shadow-2xl max-w-sm mx-4",
          "animate-scale-in",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#D97757]/20 via-[#F5B14C]/20 to-[#E08868]/20 blur-xl -z-10" />

        <div className="flex flex-col items-center text-center space-y-4">
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#D97757]/40 to-[#F5B14C]/40 animate-ping" />
            </div>
            <div
              className="relative z-10 h-20 w-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "hsl(20 70% 88%)" }}
            >
              <Flame className="h-10 w-10" style={{ color: "hsl(15 80% 45%)" }} />
            </div>
          </div>

          <div className="space-y-1">
            <p
              className="text-sm font-semibold uppercase tracking-wider"
              style={{ color: "hsl(15 80% 45%)" }}
            >
              {payload.streakCount}-week streak
            </p>
            <h2 className="text-xl font-display font-bold text-foreground leading-tight">
              {payload.title}
            </h2>
            <p className="text-sm text-muted-foreground pt-2 leading-relaxed">
              {payload.message}
            </p>
          </div>

          <p className="text-xs text-muted-foreground/70 pt-2">
            Tap anywhere to continue
          </p>
        </div>
      </div>
    </div>
  );
}
